import { takeLatest, call, put, select } from 'redux-saga/effects';
import { push } from 'connected-react-router';

// Services
import { fareTaxApi } from 'services/apiDigitalTicket/fareTax';

// Actions
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as SnackbarsActions from 'store/ducks/snackbars/actions';

// Constants
import { ERROR_CODE_MAP } from 'constants/errorCodes';
import PATH from 'constants/basePath';
import { errorMessagersList } from 'helpers/errorMessagers';

import * as FareTaxActions from './actions';
import { FareTaxTypes } from './types';

function* list({
  payload,
}: ReturnType<typeof FareTaxActions.listFareTaxRequest>): any {
  try {
    const { body } = payload;

    yield put(
      DialogsActions.setOpenLoading({
        title: 'Carregando taxas de embarque',
      }),
    );

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(fareTaxApi.get, {
      params: { transportOperatorId: transportOperator.id, ...body },
    });

    if (response.status === 200) {
      yield put(
        FareTaxActions.listFareTaxSuccess({
          fareTaxList: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );

      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode = error.response && error.response.data.internalCode;
    const { message } =
      ERROR_CODE_MAP.get(internalCode) || ERROR_CODE_MAP.get('error.generic')!;

    console.error('fareTax - listFareTax - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
        status: error.response.status,
      }),
    );
    yield put(FareTaxActions.listFareTaxFailure());

    yield put(DialogsActions.setClose());
  }
}

function* listIntegrator({
  payload,
}: ReturnType<typeof FareTaxActions.listFareTaxIntegratorRequest>): any {
  try {
    const { body } = payload;

    yield put(
      DialogsActions.setOpenLoading({
        title: 'Carregando sistemas integradores',
      }),
    );

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(fareTaxApi.getIntegrator, {
      params: { transportOperatorId: transportOperator.id, ...body },
    });

    if (response.status === 200) {
      yield put(
        FareTaxActions.listFareTaxIntegratorSuccess({
          fareTaxIntegratorList: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );

      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode = error.response && error.response.data.internalCode;
    const { message } =
      ERROR_CODE_MAP.get(internalCode) || ERROR_CODE_MAP.get('error.generic')!;

    console.error('fareTax - listFareTaxIntegrator - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
        status: error.response.status,
      }),
    );
    yield put(FareTaxActions.listFareTaxIntegratorFailure());
    yield put(DialogsActions.setClose());
  }
}

function* get({
  payload,
}: ReturnType<typeof FareTaxActions.getFareTaxRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({
        title: 'Carregando taxa de embarque',
      }),
    );

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(fareTaxApi.get, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        FareTaxActions.getFareTaxSuccess({
          fareTaxData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    console.error('fareTax - getFareTax - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível listar taxa de embarque',
      }),
    );
    yield put(FareTaxActions.getFareTaxFailure());
    yield put(DialogsActions.setClose());
  }
}

function* getIntegrator({
  payload,
}: ReturnType<typeof FareTaxActions.getFareTaxIntegratorRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({
        title: 'Carregando sistema integrador',
      }),
    );

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(fareTaxApi.getIntegrator, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        FareTaxActions.getFareTaxIntegratorSuccess({
          fareTaxIntegratorData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    console.error('fareTax - getFareTaxIntegrator - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível listar sistema integrador',
      }),
    );
    yield put(FareTaxActions.getFareTaxIntegratorFailure());
    yield put(DialogsActions.setClose());
  }
}

function* filter({
  payload,
  meta,
}: ReturnType<typeof FareTaxActions.filterFareTaxRequest>): any {
  try {
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(fareTaxApi.get, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        FareTaxActions.filterFareTaxSuccess({
          fareTaxList: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      yield put(DialogsActions.setClose());
      meta.promise.resolve(response);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('fareTax - filterFareTax - error', error);
    yield put(
      SnackbarsActions.setOpen({
        message,
        open: true,
        type: 'error',
      }),
    );
    yield put(FareTaxActions.filterFareTaxFailure());
    yield put(DialogsActions.setClose());
    meta.promise.reject(error);
  }
}

function* create({
  payload,
}: ReturnType<typeof FareTaxActions.createFareTaxRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Criando taxa de embarque' }),
    );

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(fareTaxApi.create, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        FareTaxActions.createFareTaxSuccess({
          fareTaxData: response.data,
        }),
      );

      yield put(DialogsActions.setClose());

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Taxa de embarque criada com sucesso',
        }),
      );

      yield put(push(PATH.BOARDING_FARES.BASE_URL));
    }
  } catch (error) {
    const internalCode = error.response && error.response.data.internalCode;
    const { message } =
      ERROR_CODE_MAP.get(internalCode) || ERROR_CODE_MAP.get('error.generic')!;

    console.error('fareTax - createFareTax - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(FareTaxActions.createFareTaxFailure());
    yield put(DialogsActions.setClose());
  }
}

function* createIntegrator({
  payload,
}: ReturnType<typeof FareTaxActions.createFareTaxIntegratorRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Criando sistema integrador' }),
    );

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(fareTaxApi.createIntegrator, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        FareTaxActions.createFareTaxIntegratorSuccess({
          fareTaxIntegratorData: response.data,
        }),
      );

      yield put(DialogsActions.setClose());

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Sistema integrador criado com sucesso',
        }),
      );

      yield put(
        FareTaxActions.listFareTaxIntegratorRequest({
          body: {
            _includes: 'fields',
          },
        }),
      );
    }
  } catch (error) {
    const internalCode = error.response && error.response.data.internalCode;
    const { message } =
      ERROR_CODE_MAP.get(internalCode) || ERROR_CODE_MAP.get('error.generic')!;

    console.error('fareTax - createFareTaxIntegrator - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(FareTaxActions.createFareTaxIntegratorFailure());
    yield put(DialogsActions.setClose());
  }
}

function* update({
  payload,
}: ReturnType<typeof FareTaxActions.updateFareTaxRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({
        title: 'Editando taxa de embarque',
      }),
    );

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(fareTaxApi.update, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        FareTaxActions.updateFareTaxSuccess({
          fareTaxData: response.data,
        }),
      );

      yield put(DialogsActions.setClose());

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Taxa de embarque alterada com sucesso',
        }),
      );

      yield put(push(PATH.BOARDING_FARES.BASE_URL));
    }
  } catch (error) {
    const internalCode = error.response && error.response.data.internalCode;
    const { message } =
      ERROR_CODE_MAP.get(internalCode) || ERROR_CODE_MAP.get('error.generic')!;

    console.error('fareTax - updateFareBonus - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(FareTaxActions.updateFareTaxFailure());
    yield put(DialogsActions.setClose());
  }
}

function* updateIntegrator({
  payload,
}: ReturnType<typeof FareTaxActions.updateFareTaxIntegratorRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({
        title: 'Editando sistema integrador',
      }),
    );

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(fareTaxApi.updateIntegrator, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        FareTaxActions.updateFareTaxIntegratorSuccess({
          fareTaxIntegratorData: response.data,
        }),
      );

      yield put(DialogsActions.setClose());

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Sistema integrador alterada com sucesso',
        }),
      );

      yield put(
        FareTaxActions.listFareTaxIntegratorRequest({
          body: {
            _includes: 'fields',
          },
        }),
      );
    }
  } catch (error) {
    const internalCode = error.response && error.response.data.internalCode;
    const { message } =
      ERROR_CODE_MAP.get(internalCode) || ERROR_CODE_MAP.get('error.generic')!;

    console.error('fareTax - updateFareTaxIntegrator - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(FareTaxActions.updateFareTaxIntegratorFailure());
    yield put(DialogsActions.setClose());
  }
}

function* remove({
  payload,
}: ReturnType<typeof FareTaxActions.deleteFareTaxRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Excluindo taxa de embarque' }),
    );

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(fareTaxApi.delete, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Taxa de embarque excluída com sucesso',
        }),
      );

      yield put(DialogsActions.setClose());
      yield put(FareTaxActions.deleteFareTaxSuccess());
      yield put(
        FareTaxActions.listFareTaxRequest({
          body: {
            _includes: 'value',
          },
        }),
      );
    }
  } catch (error) {
    const internalCode = error.response && error.response.data.internalCode;
    const { message } =
      ERROR_CODE_MAP.get(internalCode) || ERROR_CODE_MAP.get('error.generic')!;

    console.error('fareTax - remove - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(FareTaxActions.deleteFareTaxFailure());
    yield put(DialogsActions.setClose());
  }
}

function* removeIntegrator({
  payload,
}: ReturnType<typeof FareTaxActions.deleteFareTaxIntegratorRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Excluindo' }));

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(fareTaxApi.deleteIntegrator, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Taxa de embarque excluída com sucesso',
        }),
      );

      yield put(DialogsActions.setClose());
      yield put(FareTaxActions.deleteFareTaxIntegratorSuccess());
      yield put(
        FareTaxActions.listFareTaxIntegratorRequest({
          body: {
            _includes: 'fields',
          },
        }),
      );
    }
  } catch (error) {
    const internalCode = error.response && error.response.data.internalCode;
    const { message } =
      ERROR_CODE_MAP.get(internalCode) || ERROR_CODE_MAP.get('error.generic')!;

    console.error('fareTaxIntegrator - remove - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(FareTaxActions.deleteFareTaxIntegratorFailure());
    yield put(DialogsActions.setClose());
  }
}

// eslint-disable-next-line import/prefer-default-export
export const fareTaxSagas = [
  takeLatest(FareTaxTypes.LIST_FARE_TAX_REQUEST, list),
  takeLatest(FareTaxTypes.LIST_FARE_TAX_INTEGRATOR_REQUEST, listIntegrator),
  takeLatest(FareTaxTypes.GET_FARE_TAX_REQUEST, get),
  takeLatest(FareTaxTypes.GET_FARE_TAX_INTEGRATOR_REQUEST, getIntegrator),
  takeLatest(FareTaxTypes.FILTER_FARE_TAX_REQUEST, filter),
  takeLatest(FareTaxTypes.CREATE_FARE_TAX_REQUEST, create),
  takeLatest(FareTaxTypes.CREATE_FARE_TAX_INTEGRATOR_REQUEST, createIntegrator),
  takeLatest(FareTaxTypes.UPDATE_FARE_TAX_REQUEST, update),
  takeLatest(FareTaxTypes.UPDATE_FARE_TAX_INTEGRATOR_REQUEST, updateIntegrator),
  takeLatest(FareTaxTypes.DELETE_FARE_TAX_REQUEST, remove),
  takeLatest(FareTaxTypes.DELETE_FARE_TAX_INTEGRATOR_REQUEST, removeIntegrator),
];
